import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Barbell, ArrowRight, ArrowLeft, Check, Camera, Smiley, SmileyMeh, SmileySad } from "@phosphor-icons/react";
import GoalsStep from "@/components/GoalsStep";
import NutritionPrefsStep from "@/components/NutritionPrefsStep";

const STEPS = ["Lie", "Profile", "Body Stats", "Goals", "Photos", "Wellness", "Nutrition", "Done"];
const CONTROLLING_LIES = [
  { key: "ill_start_monday", label: "I'll start Monday.",
    truth: "You don't need a new week. You need one better decision." },
  { key: "no_time", label: "I don't have time.",
    truth: "You don't need more time. You need a simpler system." },
  { key: "tried_everything", label: "I've tried everything.",
    truth: "You've probably tried lots of diets. You haven't found a system you could stick to." },
  { key: "i_deserve_it", label: "I deserve it.",
    truth: "You deserve good health more than another temporary reward." },
  { key: "too_old", label: "I'm too old.",
    truth: "Age changes things, but it doesn't stop progress." },
  { key: "need_motivation", label: "I need motivation.",
    truth: "Motivation comes and goes. Habits keep you moving." },
  { key: "cant_afford", label: "I can't afford it.",
    truth: "The cost of doing nothing is often higher than the cost of taking action. But the right time to invest is when you're genuinely ready to commit." },
];
const EQUIPMENT_OPTIONS = [
  "Bodyweight only",
  "Dumbbells",
  "Adjustable dumbbells",
  "Kettlebells",
  "Barbell + plates",
  "Squat rack / power cage",
  "Pull-up bar",
  "Resistance bands",
  "Bench",
  "Cable machine",
  "Treadmill / bike",
  "Rowing machine",
];
const RATINGS = [
  { key: "sleep", label: "Sleep", help: "Overall sleep quality this week" },
  { key: "energy", label: "Energy", help: "Daily energy levels" },
  { key: "libido", label: "Libido", help: "Sex drive" },
  { key: "water_intake", label: "Water intake", help: "How well-hydrated do you feel?" },
  { key: "physical_activity", label: "Physical activity", help: "Movement outside of training" },
  { key: "body_image", label: "Body image", help: "How you feel about your body right now" },
  { key: "stress_levels", label: "Stress", help: "Higher = more stressed" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // mindset (step 0) — multi-select
  const [controllingLies, setControllingLies] = useState([]);
  // profile
  const [heightCm, setHeightCm] = useState("");
  const [sex, setSex] = useState("");
  const [occupation, setOccupation] = useState("");
  const [workHours, setWorkHours] = useState("");
  const [trainingLocation, setTrainingLocation] = useState("");
  const [equipment, setEquipment] = useState([]);
  const [equipmentOther, setEquipmentOther] = useState("");
  // body
  const [weightKg, setWeightKg] = useState("");
  const [waistCm, setWaistCm] = useState("");
  const [legCm, setLegCm] = useState("");
  const [armCm, setArmCm] = useState("");
  // photos
  const [photos, setPhotos] = useState({ front: null, side: null, back: null });
  const [uploading, setUploading] = useState("");
  // wellness
  const [ratings, setRatings] = useState({ sleep: 5, energy: 5, libido: 5, water_intake: 5, physical_activity: 5, body_image: 5, stress_levels: 5 });
  const [mood, setMood] = useState("flat");

  useEffect(() => { (async () => {
    try {
      const { data } = await http.get("/client/profile");
      if (data?.height_cm) setHeightCm(String(data.height_cm));
      if (data?.sex) setSex(data.sex);
      if (data?.occupation) setOccupation(data.occupation);
      if (data?.work_hours) setWorkHours(data.work_hours);
      if (data?.training_location) setTrainingLocation(data.training_location);
      if (data?.home_equipment) setEquipment(data.home_equipment);
      if (data?.home_equipment_other) setEquipmentOther(data.home_equipment_other);
      if (Array.isArray(data?.controlling_lies) && data.controlling_lies.length) {
        setControllingLies(data.controlling_lies);
      } else if (data?.controlling_lie) {
        setControllingLies([data.controlling_lie]);
      }
      if (data?.starting_photos) {
        const p = { front: null, side: null, back: null };
        for (const ph of data.starting_photos) p[ph.label] = ph.id;
        setPhotos(p);
      }
    } catch (e) { /* ignore */ }
  })(); }, []);

  const next = () => { setErr(""); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => { setErr(""); setStep((s) => Math.max(0, s - 1)); };

  const saveLie = async () => {
    if (!controllingLies.length) { setErr("Pick at least one. Be honest."); return; }
    setSaving(true); setErr("");
    try {
      await http.post("/client/mindset", { lies: controllingLies });
      next();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };
  const toggleLie = (key) => {
    setControllingLies((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  const saveProfile = async () => {
    if (!heightCm || !sex || !occupation.trim()) { setErr("Height, sex and occupation are required."); return; }
    if (!workHours) { setErr("Tell me what hours you work."); return; }
    if (!trainingLocation) { setErr("Tell me where you train so I can build the right plan."); return; }
    const showsEquip = trainingLocation === "home" || trainingLocation === "both";
    if (showsEquip && equipment.length === 0 && !equipmentOther.trim()) {
      setErr("List at least one piece of equipment you have at home (or 'none').");
      return;
    }
    setSaving(true); setErr("");
    try {
      await http.put("/client/profile", {
        height_cm: Number(heightCm),
        sex,
        occupation: occupation.trim(),
        work_hours: workHours,
        training_location: trainingLocation,
        home_equipment: showsEquip ? equipment : [],
        home_equipment_other: showsEquip ? equipmentOther.trim() : "",
      });
      next();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveBody = async () => {
    if (!weightKg || !waistCm || !legCm || !armCm) { setErr("All measurements required."); return; }
    setSaving(true); setErr("");
    try {
      await http.post("/client/body-stats", {
        weight_kg: Number(weightKg), waist_cm: Number(waistCm),
        upper_leg_cm: Number(legCm), arm_cm: Number(armCm),
      });
      next();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const onPickPhoto = async (label, file) => {
    if (!file) return;
    setErr(""); setUploading(label);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = e.target.result;
        try {
          const { data: pdata } = await http.post("/client/profile/photos", {
            label, content_type: file.type || "image/jpeg", data,
          });
          const id = (pdata.starting_photos || []).find((p) => p.label === label)?.id;
          setPhotos((cur) => ({ ...cur, [label]: id || null }));
        } catch (e) { setErr(apiError(e)); }
        finally { setUploading(""); }
      };
      reader.readAsDataURL(file);
    } catch (e) { setErr(apiError(e)); setUploading(""); }
  };

  const saveWellness = async () => {
    setSaving(true); setErr("");
    try {
      await http.post("/client/wellness", { ...ratings, mood });
      next();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#262626]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={18} color="#fff" />
            </div>
            <span className="font-oswald text-xl font-bold uppercase tracking-widest">
              Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup>
            </span>
          </div>
          <div className="id-label text-zinc-500">Onboarding · Step {Math.min(step + 1, STEPS.length - 1)} / {STEPS.length - 1}</div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-4 flex gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 ${i <= step ? "bg-[#FF3333]" : "bg-[#262626]"}`}></div>
          ))}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6" data-testid="onboarding-error">{err}</div>}

        {step === 0 && (
          <div data-testid="onboarding-step-lie">
            <div className="id-label mb-3">— Step 1 of 7 · Mindset —</div>
            <h1 className="font-oswald text-4xl sm:text-5xl uppercase tracking-tight font-bold leading-[1.05] mb-4 text-white">
              Pick one or more of the <span className="text-[#FF3333]">7 Lies</span> Men Tell Themselves
            </h1>
            <p className="text-zinc-400 font-manrope mb-8 text-base leading-relaxed">
              Which have controlled your life? Be honest — this stays between you and Jimmy. The first step to changing the story is naming it out loud.
            </p>

            <div className="space-y-3" data-testid="onb-lie-options">
              {CONTROLLING_LIES.map((l, idx) => {
                const active = controllingLies.includes(l.key);
                return (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => toggleLie(l.key)}
                    data-testid={`onb-lie-${l.key}`}
                    className={`w-full text-left px-5 py-4 border transition-colors ${
                      active
                        ? "border-[#FF3333] bg-[#FF3333]/10"
                        : "border-[#262626] hover:border-zinc-500"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span className={`mt-1 w-4 h-4 border-2 shrink-0 flex items-center justify-center transition-colors ${active ? "border-[#FF3333] bg-[#FF3333]" : "border-zinc-600"}`}>
                        {active && <Check size={10} weight="bold" color="#fff" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="id-label text-[#FF3333] mb-1">Lie #{idx + 1}</div>
                        <div className={`font-oswald text-xl sm:text-2xl uppercase tracking-tight leading-tight mb-2 ${active ? "text-white" : "text-zinc-200"}`}>
                          &ldquo;{l.label}&rdquo;
                        </div>
                        <div className="text-sm text-zinc-400 italic font-manrope leading-relaxed">
                          <span className="text-zinc-500 not-italic font-semibold">The truth:</span> {l.truth}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between gap-3">
              <div className="text-xs text-zinc-500 font-manrope">
                {controllingLies.length === 0 ? "Pick at least one" : `${controllingLies.length} selected`}
              </div>
              <button onClick={saveLie} disabled={saving || !controllingLies.length} className="id-btn-primary" data-testid="onb-next-lie">
                {saving ? "Saving..." : "Continue"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onboarding-step-profile">
            <div className="id-label mb-3">— Step 2 of 7 —</div>
            <h1 className="id-h1 mb-3">Quick profile</h1>
            <p className="text-zinc-400 font-manrope mb-8">A few one-time details so your coach can build the right plan.</p>
            <div className="space-y-5">
              <div>
                <label className="id-label block mb-2">Height (cm)</label>
                <input type="number" min={50} max={250} step={0.5} value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className="id-input" placeholder="e.g. 178" data-testid="onb-height" />
              </div>
              <div>
                <label className="id-label block mb-2">Sex</label>
                <div className="grid grid-cols-2 gap-2" data-testid="onb-sex">
                  {["male", "female"].map((s) => (
                    <button key={s} type="button" onClick={() => setSex(s)} data-testid={`onb-sex-${s}`}
                      className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${sex === s ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="id-label block mb-2">Occupation</label>
                <input value={occupation} onChange={(e) => setOccupation(e.target.value)} className="id-input" placeholder="e.g. Project manager, Tradie, etc." data-testid="onb-occupation" />
              </div>

              <div>
                <label className="id-label block mb-2">What hours do you work?</label>
                <div className="grid grid-cols-3 gap-2" data-testid="onb-work-hours">
                  {[
                    { v: "day", l: "Day" },
                    { v: "night", l: "Night" },
                    { v: "mix", l: "Mix" },
                  ].map((opt) => (
                    <button key={opt.v} type="button" onClick={() => setWorkHours(opt.v)} data-testid={`onb-work-${opt.v}`}
                      className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${workHours === opt.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div className="text-zinc-500 text-xs mt-2 font-manrope">Helps Jimmy plan around your schedule (energy, meal timing, training windows).</div>
              </div>

              <div>
                <label className="id-label block mb-2">Where do you train?</label>
                <div className="grid grid-cols-3 gap-2" data-testid="onb-training-location">
                  {[
                    { v: "gym", l: "Gym" },
                    { v: "home", l: "Home" },
                    { v: "both", l: "Both" },
                  ].map((opt) => (
                    <button key={opt.v} type="button" onClick={() => setTrainingLocation(opt.v)} data-testid={`onb-train-${opt.v}`}
                      className={`py-3 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${trainingLocation === opt.v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div className="text-zinc-500 text-xs mt-2 font-manrope">So Jimmy builds a plan that works with what you&apos;ve got.</div>
              </div>

              {(trainingLocation === "home" || trainingLocation === "both") && (
                <div data-testid="onb-equipment-section">
                  <label className="id-label block mb-2">What gear do you have at home?</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {EQUIPMENT_OPTIONS.map((eq) => {
                      const on = equipment.includes(eq);
                      return (
                        <button
                          key={eq}
                          type="button"
                          onClick={() => setEquipment((cur) => on ? cur.filter((x) => x !== eq) : [...cur, eq])}
                          data-testid={`onb-equip-${eq.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`py-2.5 px-3 border text-left text-xs font-manrope transition-colors ${on ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}
                        >
                          {eq}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    value={equipmentOther}
                    onChange={(e) => setEquipmentOther(e.target.value)}
                    className="id-input mt-3"
                    placeholder="Anything else? e.g. rings, sled, sandbag, climber..."
                    data-testid="onb-equipment-other"
                  />
                </div>
              )}
            </div>
            <div className="mt-10 flex justify-end">
              <button onClick={saveProfile} disabled={saving} className="id-btn-primary" data-testid="onb-next-profile">
                {saving ? "Saving..." : "Continue"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-body">
            <div className="id-label mb-3">— Step 3 of 7 —</div>
            <h1 className="id-h1 mb-3">Starting measurements</h1>
            <p className="text-zinc-400 font-manrope mb-8">Use a soft tape measure. Don't sweat the exact mm — consistency matters more than precision. Don't have one handy? <span className="text-white">Skip and add them later</span> — you'll get a reminder if 24 hours go by without them.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field testid="onb-weight" label="Weight (kg)" value={weightKg} set={setWeightKg} step={0.1} />
              <Field testid="onb-waist" label="Waist (cm)" value={waistCm} set={setWaistCm} step={0.1} hint="Around your belly button" />
              <Field testid="onb-leg" label="Upper leg / quad (cm)" value={legCm} set={setLegCm} step={0.1} hint="Mid-thigh, relaxed" />
              <Field testid="onb-arm" label="Bicep (cm)" value={armCm} set={setArmCm} step={0.1} hint="Mid-bicep, flexed" />
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
              <button onClick={back} className="id-btn-secondary" data-testid="onb-back-body"><ArrowLeft size={14} weight="bold" /> Back</button>
              <div className="flex gap-2">
                <button
                  onClick={next}
                  className="text-zinc-400 hover:text-white text-xs uppercase tracking-[0.15em] font-bold px-4 py-3 border border-[#262626] hover:border-zinc-500"
                  data-testid="onb-skip-body"
                >
                  Skip for now
                </button>
                <button onClick={saveBody} disabled={saving} className="id-btn-primary" data-testid="onb-next-body">
                  {saving ? "Saving..." : "Continue"} <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <GoalsStep
            initial={{ weight_kg: Number(weightKg) || undefined, waist_cm: Number(waistCm) || undefined }}
            onBack={back}
            onNext={next}
            setErr={setErr}
          />
        )}

        {step === 4 && (
          <div data-testid="onboarding-step-photos">
            <div className="id-label mb-3">— Step 5 of 7 —</div>
            <h1 className="id-h1 mb-3">Starting photos</h1>
            <p className="text-zinc-400 font-manrope mb-8">Three photos — front, side, back. Same outfit, same lighting whenever you take progress shots later. These stay private to you and your coach.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="onb-photos-grid">
              {["front", "side", "back"].map((label) => (
                <PhotoSlot
                  key={label}
                  label={label}
                  photoId={photos[label]}
                  uploading={uploading === label}
                  onPick={(file) => onPickPhoto(label, file)}
                />
              ))}
            </div>
            <p className="text-zinc-500 text-xs mt-4 font-manrope">Optional — you can skip and add later from My Stats.</p>
            <div className="mt-10 flex justify-between">
              <button onClick={back} className="id-btn-secondary" data-testid="onb-back-photos"><ArrowLeft size={14} weight="bold" /> Back</button>
              <button onClick={next} className="id-btn-primary" data-testid="onb-next-photos">Continue <ArrowRight size={14} weight="bold" /></button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="onboarding-step-wellness">
            <div className="id-label mb-3">— Step 6 of 7 —</div>
            <h1 className="id-h1 mb-3">Wellness baseline</h1>
            <p className="text-zinc-400 font-manrope mb-8">Rate yourself 0–10 on each. You'll update this every fortnight to track how the work is changing you.</p>

            <div className="space-y-5">
              {RATINGS.map((r) => (
                <RatingRow key={r.key} testid={`onb-rating-${r.key}`} label={r.label} help={r.help}
                  value={ratings[r.key]} set={(v) => setRatings((cur) => ({ ...cur, [r.key]: v }))} />
              ))}

              <div>
                <div className="id-label mb-3">Mood</div>
                <div className="grid grid-cols-3 gap-2" data-testid="onb-mood">
                  {[
                    { v: "sad", Icon: SmileySad, l: "Sad" },
                    { v: "flat", Icon: SmileyMeh, l: "Flat" },
                    { v: "happy", Icon: Smiley, l: "Happy" },
                  ].map(({ v, Icon, l }) => (
                    <button key={v} type="button" onClick={() => setMood(v)} data-testid={`onb-mood-${v}`}
                      className={`py-4 border flex flex-col items-center gap-1 transition-colors ${mood === v ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                      <Icon size={28} weight="bold" />
                      <span className="text-xs uppercase tracking-[0.15em] font-bold">{l}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-10 flex justify-between">
              <button onClick={back} className="id-btn-secondary" data-testid="onb-back-wellness"><ArrowLeft size={14} weight="bold" /> Back</button>
              <button onClick={saveWellness} disabled={saving} className="id-btn-primary" data-testid="onb-next-wellness">
                {saving ? "Saving..." : "Continue"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 6 && (
          <NutritionPrefsStep
            onBack={back}
            onNext={next}
            setErr={setErr}
          />
        )}

        {step === 7 && (
          <div className="text-center py-16" data-testid="onboarding-step-done">
            <div className="w-20 h-20 mx-auto bg-[#FF3333] flex items-center justify-center mb-6">
              <Check size={40} weight="bold" color="#fff" />
            </div>
            <h1 className="id-h1 mb-2">You're locked in.</h1>
            <p className="text-zinc-400 font-manrope mb-10">Your coach can see everything now. Time to work.</p>
            <button onClick={() => navigate("/app", { replace: true })} className="id-btn-primary" data-testid="onb-enter-app">
              Enter portal <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, set, step = 1, hint, testid }) {
  return (
    <div>
      <label className="id-label block mb-2">{label}</label>
      <input type="number" min={0} step={step} value={value} onChange={(e) => set(e.target.value)} className="id-input" data-testid={testid} />
      {hint && <div className="text-zinc-500 text-xs mt-1 font-manrope">{hint}</div>}
    </div>
  );
}

function RatingRow({ label, help, value, set, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="font-oswald uppercase tracking-wide text-base">{label}</div>
          {help && <div className="text-zinc-500 text-xs font-manrope">{help}</div>}
        </div>
        <div className="font-oswald text-2xl text-[#FF3333] font-bold">{value}</div>
      </div>
      <input type="range" min={0} max={10} step={1} value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full accent-[#FF3333]"
        data-testid={`${testid}-slider`} />
      <div className="flex justify-between text-[10px] text-zinc-600 uppercase tracking-[0.15em] mt-1">
        <span>0 worst</span><span>10 best</span>
      </div>
    </div>
  );
}

function PhotoSlot({ label, photoId, uploading, onPick }) {
  const inputRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const src = photoId ? `${apiBase}/api/client/profile/photos/${photoId}` : null;
  return (
    <div className="border border-[#262626] aspect-[3/4] relative overflow-hidden bg-[#0F0F0F]" data-testid={`onb-photo-${label}`}>
      {src ? (
        <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600">
          <Camera size={36} weight="bold" />
        </div>
      )}
      <div className="absolute top-3 left-3 id-label text-zinc-300 bg-[#0A0A0A]/80 px-2 py-0.5">{label}</div>
      <button onClick={() => inputRef.current?.click()} disabled={uploading}
        className="absolute bottom-0 inset-x-0 bg-[#FF3333]/95 hover:bg-[#CC0000] text-white py-2 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-60"
        data-testid={`onb-photo-pick-${label}`}>
        {uploading ? "Uploading..." : (src ? "Replace" : "Upload")}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
    </div>
  );
}
